import React, {  } from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {TFunction} from 'react-i18next';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import { Permission } from '../../store/types/Permission';
import { Colors } from '../../styles/Colors';
import { PhoneDirectoryRecord } from '../../store/types/Devices';
import CustomizedIconButton from '../../components/IconButton/IconButton';
import { Column } from 'react-table';
import PermissionLinkCell from '../../components/DataTable/PermissionLinkCell';
import {Delete, Edit} from '@material-ui/icons';
import OverflowTooltip from '../../components/OverflowTooltip/OverflowTooltip';

export interface DevicePhoneBookDetailsProps {
    id: number;
}

export const useStyle = makeStyles(() => ({

    loader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        flex: 1,
    },
    
    mainContainer: {
        height: '100vh',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        background: Colors.SmokeBackground,

        '& .MuiButton-label': {
            color: Colors.White,
        },
    },

    scrollable: {
        overflowY: 'auto',
        flex: 1,
        padding: '50px 90px 120px 90px',
    },

    headerContainer: {
        display: 'flex',
        justifyContent: 'row'
    },
    
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: 10,
    },

    titleContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginRight: 25,
        '& h2': {
            fontSize: 32,
            lineHeight: '24px',
            marginBlockEnd: 8,
            marginBlockStart: 4,
            fontWeight: 500,
        },
        '& p': {
            fontSize: 14,
            lineHeight: '16px',
            opacity: 0.38,
            marginBlockStart: 0,
            marginBottom: 0,
        },
        '& .MuiButtonBase-root': {
            marginTop: '24px',
            marginBottom: '48px',
            width: '135px',
        },
    },

    addNewButton: {
        marginTop: 8,
        width: 'fit-content',
        whiteSpace: 'nowrap',
    },

    chevron: {
        display: 'flex',
    },

    arrow: {
        cursor: 'pointer',
    },

    tableContainer: {
        maxWidth: 1040,
        overflowX: 'hidden',
    },

    table: {
        '& tr th.MuiTableCell-root.MuiTableCell-head:nth-child(2)': {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& tr td.MuiTableCell-root:nth-child(2)': {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& tr th.MuiTableCell-root.MuiTableCell-head:nth-child(3)': {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& tr td.MuiTableCell-root:nth-child(3)': {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& tr th.MuiTableCell-root.MuiTableCell-head:nth-child(4)': {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& tr td.MuiTableCell-root:nth-child(4)': {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& tr th.MuiTableCell-root.MuiTableCell-head:nth-child(5)': {
            textAlign: 'center',
            paddingLeft: 0,
            paddingRight: 16,
        },
        '& tr td.MuiTableCell-root:nth-child(5)': {
            paddingLeft: 0,
            textAlign: 'center'
        },
    },
    
    actionCell: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        width: '100%'
    },
    
    deleteDialog: {
        '& .MuiPaper-root': {
            maxWidth: '365px !important',
        },

        '& .MuiDialogContent-root': {
            minWidth: 'unset',
        },
    },

    contentDialog: {
        backgroundColor: Colors.White + ' !important',
        wordBreak: 'break-word'
    },
    
    actionButtonsContainer: {
        top: 'calc(100vh - 96px)',
        left: 91,
        maxWidth: 991,
        width: 'calc(100v2 - 91px - 24px)',
    },

    saveButtonClass: {
        width: 'fit-content',
        height: 36,
        borderRadius: 4,
        fontSize: 16,
        textWrapMode: 'nowrap'
    },

    lastUpdatedContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        height: 'fit-content',
        width: 'fit-content',
        paddingTop: 10,
    },

    infoIcon: {
        color: `rgba(176, 176, 176, 1) !important`,
        '& svg': {
            '& path': {
                fill: `rgba(176, 176, 176, 1) !important`,
                fillOpacity: 1,
            },
        },
    },

    infoText: {
        fontWeight: 400,
        fontSize: 16,
        lineHeight: '20px',
        verticalAlign: 'middle',
        color: 'rgba(70, 70, 70, 1)',
        marginLeft: 8,
        paddingTop: 2,
        marginRight: 16,
    },

    dropdownMenuItem: {
        fontSize: 16,
        color: Colors.Text
    },

    moreMenuButton: {
        marginTop: 8,
    }
}));

export const generateColumns = (
    t: TFunction<string>,
    classes: ReturnType<typeof useStyle>,
    onDelete: (obj: PhoneDirectoryRecord) => void,
    isAutoSyncDirectory: boolean,
    onEdit: (editRecord: PhoneDirectoryRecord | undefined) => void
): Column<PhoneDirectoryRecord>[] => {

    const drawValueSpan = (text?: string) => {
        return (
            <div style={{overflow: 'hidden'}} data-testid={'phonebookentry-value-span'}>
                <OverflowTooltip
                    tooltip={text}
                    text={text}
                    copy={false}
                />
            </div>
        );
    }

    return [
        {
            accessor: 'first_name',
            Header: t<string>('screens:devicePhoneBooks.name'),
            width: 1,
            Cell: function Cell(params) {
                const name = (params.row.original.first_name + ' ' + params.row.original.last_name)?.trim();
                return (
                    <PermissionLinkCell
                        text={name || ''}
                        onClick={() => {
                            onEdit?.(params.row.original);
                        }}
                        permissions={Permission.Inventory.DevicePhoneBooks.value}
                    />
                );
            },
        },
        {
            accessor: 'mobile_number',
            width: 1,
            Header: t<string>('screens:devicePhoneBooks.mobileNumber'),
            Cell: function Cell(params) {
                return drawValueSpan(params.row.original.mobile_number);
            },
        },
        {
            accessor: 'office_number',
            width: 1,
            Header: t<string>('screens:devicePhoneBooks.officeNumber'),
            Cell: function Cell(params) {
                return drawValueSpan(params.row.original.office_number);
            },
        },
        {
            accessor: 'other_number',
            width: 1,
            Header: t<string>('screens:devicePhoneBooks.otherNumber'),
            Cell: function Cell(params) {
                return drawValueSpan(params.row.original.other_number);
            },
        },
        {
            accessor: 'i_ua_config_directory_item',
            width: 1.5,
            Header: t<string>('common:actions'),
            Cell: function Cell(params) {
                return (<div className={classes.actionCell}>
                        <PermissionProvider
                            permission={
                                Permission.Inventory.DevicePhoneBooks.value
                            }
                            skipReadOnly
                        >
                            <CustomizedIconButton
                                onClick={() => {
                                    onEdit?.(params.row.original);
                                }}
                                tooltipText={isAutoSyncDirectory ? t<string>('errors:devicePhoneBooks.cantUpdateAutoSyncDirectory') : t<string>('common:edit')}
                                dataTestId="edit-list-item-button"
                                dataQa="edit-list-item-button"
                                disabled={isAutoSyncDirectory}
                            >
                                <Edit htmlColor={Colors.ListIconColor}/>
                            </CustomizedIconButton>
                        </PermissionProvider>
                        <PermissionProvider
                            permission={
                                Permission.Inventory.DevicePhoneBooks.value
                            }
                        >
                            <CustomizedIconButton
                                onClick={() =>{
                                    onDelete?.(params.row.original)
                                }}
                                tooltipText={isAutoSyncDirectory ? t<string>('errors:devicePhoneBooks.cantUpdateAutoSyncDirectory') : t<string>('common:delete')}
                                dataTestId="remove-list-item-button"
                                dataQa="remove-list-item-button"
                                disabled={isAutoSyncDirectory}
                            >
                                <Delete htmlColor={Colors.ListIconColor}/>
                            </CustomizedIconButton>
                        </PermissionProvider>
                    </div>
                );
            },
        }
    ];
};